import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import {
  MAX_ALLOWED_DESTINATIONS,
  e164Schema,
  enableTelephonyBodySchema,
  telephonySettingsBodySchema,
  type PlatformOrganization,
  type TelephonyConnectionView,
  type TelephonyProviderName,
  type VerifyCallerResponse,
} from "@gioia365/contracts";
import type { MessageKey } from "@gioia365/i18n";
import { useQuery } from "@tanstack/react-query";
import { ApiRequestError } from "../../api/http";
import {
  TELEPHONY_REFRESH_MS,
  platformTelephonyQuery,
  useCheckTelephony,
  useDisableTelephony,
  useEnableTelephony,
  useProvisionTelephony,
  useSaveTelephonySettings,
  useVerifyCaller,
} from "../../api/queries";
import { Button } from "../../design/components/Button";
import { Checkbox } from "../../design/components/Checkbox";
import { Dialog } from "../../design/components/Dialog";
import { Icon, type IconName } from "../../design/components/Icon";
import { Reason } from "../../design/components/Reason";
import { Select } from "../../design/components/Select";
import { StatusPill } from "../../design/components/StatusPill";
import { TextArea } from "../../design/components/TextArea";
import { TextField } from "../../design/components/TextField";
import { messageForError } from "../../i18n/errors";
import { deviceTimeZone, formatNumericDateTime } from "../../i18n/format";
import { useT, type Translate } from "../../i18n/locale";
import { CallerVerification, isVerifying } from "../../telephony/caller-verification";
import { italianLandlinesWithoutZero, parseAllowedNumbers, withoutSeparators } from "./allowed-numbers";
import { allowedOf, isDirty, noticeOf, reconcile, startForm, type SettingsDraft, type SettingsForm } from "./settings-draft";
import "./platform.css";

// «Telefonia» di un'azienda (piano M4, Task 14, senza mock-up: segue lo stile di «Gestisci»): il Super admin di Gioia365
// la accende e la spegne, segue i passi che il server fa da solo presso il fornitore (D18), verifica il numero mostrato,
// sceglie registrazione, avviso e numeri ammessi, e prova la connessione. Non c'è mai un campo per una chiave o un segreto:
// le credenziali le crea e le custodisce il server, e la vista che il server manda non le contiene.
//
// Il pannello è una finestra modale, quindi ciò che si decide qui e fallisce si spiega qui dentro (D16). Mentre il
// provisioning è in corso, o una verifica del numero non è ancora confermata, rilegge lo stato ogni 3 secondi; mentre
// l'accensione o «Riprova» aspettano la risposta del server (la saga dura qualche secondo) mostra i passi che il server
// ha già scritto. Le rotte sono quelle del Task 5 (`/api/platform/organizations/:id/telephony`).

/** Le aziende di prova dello staging si accendono soltanto con i numeri ammessi (D12): le riconosce lo slug, come il server. */
const TRIAL_SLUG_PREFIX = "prova-";

/** I fornitori che il server sa impostare. Telnyx ha il suo adattatore soltanto se vince la prova di R1 (Task 2 e 3). */
const PROVIDERS: readonly TelephonyProviderName[] = ["twilio"];
const PROVIDER_LABEL: Record<TelephonyProviderName, MessageKey> = { twilio: "platformTelephony.providers.twilio", telnyx: "platformTelephony.providers.telnyx" };

type Provisioning = TelephonyConnectionView["provisioning"];
type Step = Exclude<Provisioning["step"], "none">;
/** I passi del provisioning nell'ordine in cui il server li fa. */
const STEPS: readonly Step[] = ["account", "api_key", "application", "storage"];
const STEP_DONE: Record<Step, MessageKey> = {
  account: "platformTelephony.stepAccount",
  api_key: "platformTelephony.stepApiKey",
  application: "platformTelephony.stepApplication",
  storage: "platformTelephony.stepStorage",
};
const STEP_NAME: Record<Step, MessageKey> = {
  account: "platformTelephony.stepName.account",
  api_key: "platformTelephony.stepName.api_key",
  application: "platformTelephony.stepName.application",
  storage: "platformTelephony.stepName.storage",
};

/** `atProvider`: il passo dello spazio a telefonia pronta senza lo spazio di Gioia365, perché l'audio resta presso il fornitore. */
type StepState = "done" | "doing" | "waiting" | "failed" | "atProvider";
const STEP_ICON: Record<StepState, IconName> = { done: "check", doing: "clock", waiting: "dot", failed: "alert", atProvider: "check" };

/** Il provisioning di una telefonia appena accesa: ancora nessun passo fatto. */
const JUST_STARTED: Provisioning = { state: "pending", step: "none", failedStep: null, error: null, recordingStorage: "not_configured", stalled: false };

/** Un problema da mostrare, col testo calcolato quando si mostra: segue la lingua dell'interfaccia. */
type Problem = { field: string; text: (t: Translate) => string };

/**
 * Come sta ogni passo. Mentre il server lavora (`working`) il giro è in corso anche se la vista dice ancora «non riuscita».
 * A telefonia pronta il passo dello spazio senza lo spazio di Gioia365 non è un passo mancato: dal 4 ottobre 2026 le
 * registrazioni restano presso il fornitore, e la riga lo dice con il segno dei passi fatti.
 */
function stepStates(provisioning: Provisioning, working: boolean): Record<Step, StepState> {
  const state = working ? "pending" : provisioning.state;
  const lastDone = provisioning.step === "none" ? -1 : STEPS.indexOf(provisioning.step);
  const failedStep = provisioning.failedStep ?? STEPS[lastDone + 1];
  const result = {} as Record<Step, StepState>;
  STEPS.forEach((step, index) => {
    if (state === "ready") result[step] = step === "storage" && provisioning.recordingStorage !== "configured" ? "atProvider" : "done";
    else if (index <= lastDone) result[step] = "done";
    else if (state === "failed" && step === failedStep) result[step] = "failed";
    else result[step] = state === "pending" && index === lastDone + 1 ? "doing" : "waiting";
  });
  return result;
}

/**
 * Il motivo di un passo non riuscito a parole, dal codice che il server ha registrato (mai il messaggio del fornitore). Chi
 * legge è il titolare, non uno sviluppatore: ogni codice che si conosce ha la sua frase, e uno che non si conosce vale la frase
 * generica. Il codice grezzo non si mostra mai.
 */
function provisioningReason(t: Translate, code: string | null): string {
  if (code === null) return t("platformTelephony.reason.generic");
  if (code === "http_401" || code === "http_403" || code === "20003") return t("platformTelephony.reason.credentials");
  if (code === "http_429" || code === "20429") return t("platformTelephony.reason.rateLimited");
  if (/^http_5\d\d$/.test(code)) return t("platformTelephony.reason.providerDown");
  switch (code) {
    case "unreachable":
      return t("platformTelephony.reason.unreachable");
    case "timeout":
      return t("platformTelephony.reason.timeout");
    case "account_missing":
      return t("platformTelephony.reason.accountMissing");
    case "not_a_company_account":
      return t("platformTelephony.reason.foreignAccount");
    case "application_missing":
      return t("platformTelephony.reason.applicationMissing");
    case "invalid_response":
      return t("platformTelephony.reason.invalidResponse");
    case "signing_secret_missing":
      return t("platformTelephony.reason.signingSecret");
    case "secrets_unreadable":
      return t("platformTelephony.reason.secrets");
    default:
      // «provider_error» e ogni altro codice, anche numerico, che il fornitore possa dare.
      return t("platformTelephony.reason.generic");
  }
}

/** Il motivo di una prova non riuscita a parole (`ConnectionCheck` del dominio); un codice nuovo vale la frase generica, mai il codice. */
function checkReason(t: Translate, code: string): string {
  switch (code) {
    case "credentials_rejected":
      return t("platformTelephony.checkReason.credentials_rejected");
    case "account_mismatch":
      return t("platformTelephony.checkReason.account_mismatch");
    case "application_missing":
      return t("platformTelephony.checkReason.application_missing");
    case "caller_not_verified":
      return t("platformTelephony.checkReason.caller_not_verified");
    case "unreachable":
      return t("platformTelephony.checkReason.unreachable");
    default:
      return t("platformTelephony.checkReason.other");
  }
}

/** Il pannello si rilegge ogni 3 secondi finché il provisioning è in corso o una verifica non è confermata. */
function isFollowing(view: TelephonyConnectionView | null | undefined): boolean {
  return view?.status === "active" && (view.provisioning.state === "pending" || isVerifying(view.defaultCallerVerification));
}

type Props = { organization: PlatformOrganization; onClose: () => void };

export function OrganizationTelephonyDialog({ organization, onClose }: Props) {
  const t = useT();
  const baseId = useId();
  const { id: organizationId, name } = organization;
  const enable = useEnableTelephony(organizationId);
  const provision = useProvisionTelephony(organizationId);
  const disable = useDisableTelephony(organizationId);
  // Il server sta lavorando: la risposta arriva alla fine della saga, e nel frattempo il pannello rilegge i passi.
  const working = enable.isPending || provision.isPending;
  const query = useQuery({
    ...platformTelephonyQuery(organizationId),
    refetchInterval: (current) => (working || isFollowing(current.state.data) ? TELEPHONY_REFRESH_MS : false),
  });
  const view = query.data;
  const isTrial = organization.slug.startsWith(TRIAL_SLUG_PREFIX);
  // Il pannello si apre su ogni azienda, ma il server accende, riprende e verifica soltanto in un'azienda attiva (409
  // `invalid_state`): lì i pulsanti sono spenti e il motivo è scritto. Vedere, provare, salvare e spegnere si può sempre.
  const inactive = organization.status !== "active";

  // Il modulo di accensione vive qui, non nel suo pezzo: mentre il server lavora il modulo non c'è, e se l'accensione viene
  // rifiutata deve tornare con ciò che era scritto.
  const [provider, setProvider] = useState<TelephonyProviderName>(PROVIDERS[0] ?? "twilio");
  const [allowedText, setAllowedText] = useState("");
  const [enableProblem, setEnableProblem] = useState<Problem | null>(null);
  const [retryProblem, setRetryProblem] = useState<Problem | null>(null);
  const [confirmingOff, setConfirmingOff] = useState(false);
  const [offProblem, setOffProblem] = useState<Problem | null>(null);

  // Il fuoco dopo un cambio che toglie dalla pagina il pulsante premuto (la conferma, «Spegni la telefonia»): dopo il
  // disegno che segue il cambio, e finché il controllo non c'è.
  const focusTarget = useRef<string | null>(null);
  useEffect(() => {
    if (focusTarget.current === null) return;
    const element = document.getElementById(focusTarget.current);
    if (!element) return;
    element.focus();
    focusTarget.current = null;
  });
  const setFocusTarget = (id: string) => {
    focusTarget.current = id;
  };
  const turnOffId = `${baseId}-turn-off`;
  const cancelOffId = `${baseId}-cancel-off`;
  const turnOnId = `${baseId}-turn-on`;
  const allowedId = `${baseId}-enable-allowed`;
  const inactiveId = `${baseId}-inactive`;

  function failWith(setProblem: (problem: Problem | null) => void, field: string, error: unknown) {
    setProblem({ field, text: (tr) => messageForError(tr, error) });
  }

  /** «Accendi la telefonia»: con i numeri ammessi dove servono, altrimenti con quelli che la connessione ha già. */
  async function turnOn() {
    if (working || inactive) return;
    const asksNumbers = view === null || view === undefined || (isTrial && view.allowedDestinations === null);
    let allowedDestinations: string[] | null = view?.allowedDestinations ?? null;
    if (asksNumbers) {
      const parsed = parseAllowedNumbers(allowedText);
      if (!parsed.ok) {
        setEnableProblem(
          parsed.problem === "invalid"
            ? { field: "allowed", text: (tr) => tr("platformTelephony.allowedInvalid", { number: parsed.number }) }
            : { field: "allowed", text: (tr) => tr("platformTelephony.allowedTooMany", { max: MAX_ALLOWED_DESTINATIONS }) },
        );
        document.getElementById(allowedId)?.focus();
        return;
      }
      if (parsed.numbers.length === 0 && isTrial) {
        setEnableProblem({ field: "allowed", text: (tr) => tr("platformTelephony.allowedMissing") });
        document.getElementById(allowedId)?.focus();
        return;
      }
      allowedDestinations = parsed.numbers.length === 0 ? null : parsed.numbers;
    }
    const body = enableTelephonyBodySchema.safeParse({ provider: view?.provider ?? provider, allowedDestinations });
    if (!body.success) {
      setEnableProblem({ field: "form", text: (tr) => tr("errors.validation_failed") });
      return;
    }
    setEnableProblem(null);
    try {
      await enable.mutateAsync(body.data);
    } catch (error) {
      // Un passo del provisioning che si ferma lascia la telefonia «non riuscita»: la vista del rifiuto è già nella cache e il
      // pannello la mostra con il suo «Riprova». Ogni altro rifiuto si spiega sul modulo.
      if (!(error instanceof ApiRequestError && error.code === "telephony_provisioning_failed")) failWith(setEnableProblem, "form", error);
    }
  }

  async function retry() {
    if (working || inactive) return;
    setRetryProblem(null);
    try {
      await provision.mutateAsync();
    } catch (error) {
      if (!(error instanceof ApiRequestError && error.code === "telephony_provisioning_failed")) failWith(setRetryProblem, "retry", error);
    }
  }

  async function turnOff() {
    setOffProblem(null);
    try {
      await disable.mutateAsync();
      setConfirmingOff(false);
      setFocusTarget(turnOnId);
    } catch (error) {
      failWith(setOffProblem, "off", error);
    }
  }

  function askToTurnOff() {
    setOffProblem(null);
    setConfirmingOff(true);
    setFocusTarget(cancelOffId);
  }

  function cancelTurnOff() {
    setConfirmingOff(false);
    setOffProblem(null);
    setFocusTarget(turnOffId);
  }

  const active = view?.status === "active" ? view : null;
  // Accesa o in preparazione: la vista del server; appena premuto «Accendi» (prima che il server abbia scritto la riga, o
  // mentre la riaccende) non ce n'è ancora una, e i passi partono da zero.
  const provisioning: Provisioning | null = active ? active.provisioning : working ? JUST_STARTED : null;
  const providerName = view?.provider ?? provider;

  let content: ReactNode;
  if (query.isError && view === undefined) {
    content = (
      <div className="g-platform__problem">
        <Reason tone="warning" role="alert">
          {messageForError(t, query.error)}
        </Reason>
        <Button onClick={() => void query.refetch()}>{t("common.retry")}</Button>
      </div>
    );
  } else if (view === undefined) {
    content = <p className="g-platform__counts">{t("common.loading")}</p>;
  } else if (provisioning === null) {
    content = (
      <EnableForm
        baseId={baseId}
        turnOnId={turnOnId}
        allowedId={allowedId}
        provider={provider}
        onProvider={setProvider}
        existing={view}
        isTrial={isTrial}
        blockedId={inactive ? inactiveId : null}
        allowedText={allowedText}
        onAllowedText={(text) => {
          setAllowedText(text);
          setEnableProblem(null);
        }}
        problem={enableProblem}
        onTurnOn={() => void turnOn()}
      />
    );
  } else {
    content = (
      <div className="g-telephony">
        {inactive ? (
          <Reason id={inactiveId} tone="warning">
            {t("platformTelephony.inactive")}
          </Reason>
        ) : null}
        <section className="g-telephony__section" aria-labelledby={`${baseId}-setup`}>
          <h3 id={`${baseId}-setup`}>{t("platformTelephony.setupTitle")}</h3>
          <dl className="g-telephony__facts">
            <div>
              <dt>{t("platformTelephony.provider")}</dt>
              <dd>{t(PROVIDER_LABEL[providerName])}</dd>
            </div>
          </dl>
          <ProvisioningProgress
            provisioning={provisioning}
            working={working}
            retrying={provision.isPending}
            blockedId={inactive ? inactiveId : null}
            problem={retryProblem}
            onRetry={() => void retry()}
          />
        </section>
        {active && !working && active.provisioning.state === "ready" ? (
          <ReadySections baseId={baseId} organizationId={organizationId} view={active} isTrial={isTrial} blockedId={inactive ? inactiveId : null} />
        ) : (
          <Reason>{t("platformTelephony.waitReady")}</Reason>
        )}
        {confirmingOff ? (
          <section className="g-telephony__confirm" aria-labelledby={`${baseId}-off-title`}>
            <h3 id={`${baseId}-off-title`}>{t("platformTelephony.disableTitle", { organization: name })}</h3>
            <p>{t("platformTelephony.disableText")}</p>
            {offProblem ? (
              <Reason tone="warning" role="alert">
                {offProblem.text(t)}
              </Reason>
            ) : null}
            <div className="g-telephony__confirm-actions">
              <Button id={cancelOffId} onClick={cancelTurnOff} disabled={disable.isPending}>
                {t("common.cancel")}
              </Button>
              <Button variant="primary" onClick={() => void turnOff()} disabled={disable.isPending} aria-busy={disable.isPending ? "true" : undefined}>
                {disable.isPending ? t("platformTelephony.disabling") : t("platformTelephony.disable")}
              </Button>
            </div>
          </section>
        ) : null}
      </div>
    );
  }

  return (
    <Dialog
      open
      size="wide"
      title={t("platformTelephony.title", { organization: name })}
      onClose={onClose}
      footer={
        <>
          {active && !confirmingOff ? (
            <Button id={turnOffId} variant="ghost" onClick={askToTurnOff}>
              {t("platformTelephony.disable")}
            </Button>
          ) : null}
          <span className="g-platform__spacer" />
          <Button onClick={onClose}>{t("platform.manage.done")}</Button>
        </>
      }
    >
      {content}
    </Dialog>
  );
}

/**
 * Sotto «Numeri ammessi»: un avviso per ogni numero scritto che sembra un fisso italiano senza lo 0 (prova del 5 ottobre 2026,
 * `+3995…` al posto di `+39095…`). Soltanto un avviso, nello stile degli altri del pannello e senza `role="alert"`: il numero
 * si salva lo stesso (regola dei pochi blocchi). Si ricalcola dal testo a ogni tasto; finché il testo non si legge (un numero
 * non valido, troppi numeri) tace, perché quel problema lo dice il salvataggio.
 */
function LandlineWarnings({ text }: { text: string }) {
  const t = useT();
  const parsed = parseAllowedNumbers(text);
  if (!parsed.ok) return null;
  return (
    <>
      {italianLandlinesWithoutZero(parsed.numbers).map((number) => (
        <Reason key={number} tone="warning">
          {t("platformTelephony.allowedLandline", { number })}
        </Reason>
      ))}
    </>
  );
}

type EnableFormProps = {
  baseId: string;
  turnOnId: string;
  allowedId: string;
  provider: TelephonyProviderName;
  onProvider: (provider: TelephonyProviderName) => void;
  /** La connessione spenta, se c'è: si riaccende con il suo fornitore e i suoi numeri ammessi. */
  existing: TelephonyConnectionView | null;
  isTrial: boolean;
  /** L'`id` del testo che dice perché non si può accendere (azienda non attiva), o `null`. */
  blockedId: string | null;
  allowedText: string;
  onAllowedText: (text: string) => void;
  problem: Problem | null;
  onTurnOn: () => void;
};

/** La telefonia è spenta (o non è mai stata accesa): «Accendi la telefonia» chiede il fornitore e, dove servono, i numeri ammessi. */
function EnableForm({ baseId, turnOnId, allowedId, provider, onProvider, existing, isTrial, blockedId, allowedText, onAllowedText, problem, onTurnOn }: EnableFormProps) {
  const t = useT();
  // Una connessione spenta ha già il suo fornitore e i suoi numeri ammessi: la riaccensione non li tocca.
  // In un'azienda non attiva non si accende: nessun modulo da compilare per qualcosa che il server rifiuterebbe.
  const asksProvider = existing === null && blockedId === null;
  const asksNumbers = blockedId === null && (existing === null || (isTrial && existing.allowedDestinations === null));
  const allowedProblem = problem?.field === "allowed" ? problem.text(t) : undefined;
  const formProblem = problem?.field === "form" ? problem.text(t) : undefined;
  return (
    <form
      className="g-stack"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        onTurnOn();
      }}
    >
      <p>{t("platformTelephony.off")}</p>
      <p>{t("platformTelephony.turnOnHint")}</p>
      {asksProvider ? (
        <Select
          id={`${baseId}-provider`}
          label={t("platformTelephony.provider")}
          value={provider}
          onChange={(value) => {
            const next = PROVIDERS.find((candidate) => candidate === value);
            if (next) onProvider(next);
          }}
          options={PROVIDERS.map((value) => ({ value, label: t(PROVIDER_LABEL[value]) }))}
        />
      ) : null}
      {asksNumbers ? (
        <>
          <TextArea
            id={allowedId}
            label={t("platformTelephony.allowed")}
            value={allowedText}
            rows={4}
            autoComplete="off"
            spellCheck={false}
            hint={`${t("platformTelephony.allowedHint")} ${t("platformTelephony.allowedFormat", { max: MAX_ALLOWED_DESTINATIONS })}`}
            error={allowedProblem}
            onChange={(event) => onAllowedText(event.target.value)}
          />
          <LandlineWarnings text={allowedText} />
        </>
      ) : null}
      {formProblem ? (
        <Reason tone="warning" role="alert">
          {formProblem}
        </Reason>
      ) : null}
      {blockedId !== null ? <Reason id={blockedId}>{t("platformTelephony.inactiveTurnOn")}</Reason> : null}
      <div>
        <Button id={turnOnId} variant="primary" type="submit" disabled={blockedId !== null} aria-describedby={blockedId ?? undefined}>
          {t("platformTelephony.turnOn")}
        </Button>
      </div>
    </form>
  );
}

type ProgressProps = {
  provisioning: Provisioning;
  /** Il server sta lavorando (accensione o «Riprova»). */
  working: boolean;
  /** È «Riprova» che sta lavorando. */
  retrying: boolean;
  /** L'`id` del testo che dice perché «Riprova» è spento (azienda non attiva), o `null`. */
  blockedId: string | null;
  problem: Problem | null;
  onRetry: () => void;
};

/**
 * I passi che il server fa da solo presso il fornitore, con lo stato: «In preparazione…», «Pronta» o «Non riuscita…» con
 * «Riprova»; «Riprova» anche per un giro rimasto a metà da più di cinque minuti (`stalled`: un deploy o un processo morto
 * durante l'accensione), che il giro di riconciliazione del server riprende comunque da sé.
 */
function ProvisioningProgress({ provisioning, working, retrying, blockedId, problem, onRetry }: ProgressProps) {
  const t = useT();
  const states = stepStates(provisioning, working);
  const state = working ? "pending" : provisioning.state;
  const stalled = !working && state === "pending" && provisioning.stalled;
  const failedStep = provisioning.failedStep ?? STEPS.find((step) => states[step] === "failed") ?? null;
  const stepText = (step: Step): string => {
    switch (states[step]) {
      case "done":
        return t(STEP_DONE[step]);
      case "atProvider":
        return t("platformTelephony.stepAudioAtProvider");
      case "doing":
        return t("platformTelephony.stepDoing", { step: t(STEP_NAME[step]) });
      case "failed":
        return t("platformTelephony.stepFailed", { step: t(STEP_NAME[step]) });
      case "waiting":
        return t("platformTelephony.stepWaiting", { step: t(STEP_NAME[step]) });
    }
  };
  return (
    <>
      {/* Sempre nella pagina e vuota finché non serve: uno screen reader annuncia ogni cambio di stato. */}
      <div role="status">
        {state === "pending" ? (
          <StatusPill tone="warning" icon="clock">
            {t("platformTelephony.statePending")}
          </StatusPill>
        ) : null}
        {state === "ready" ? (
          <StatusPill tone="success" icon="check">
            {t("platformTelephony.stateReady")}
          </StatusPill>
        ) : null}
      </div>
      {working ? <p className="g-platform__wait">{t("platformTelephony.turningOnWait")}</p> : null}
      <ol className="g-telephony__steps" aria-label={t("platformTelephony.setupTitle")}>
        {STEPS.map((step) => (
          <li key={step} className="g-telephony__step" data-state={states[step]}>
            <Icon name={STEP_ICON[states[step]]} />
            <span>{stepText(step)}</span>
          </li>
        ))}
      </ol>
      {state === "failed" || stalled ? (
        <>
          <Reason tone="warning" role="alert">
            {stalled
              ? t("platformTelephony.stateStalled")
              : t("platformTelephony.stateFailed", {
                  step: failedStep === null ? "" : t(STEP_NAME[failedStep]),
                  reason: provisioningReason(t, provisioning.error),
                })}
          </Reason>
          {problem ? (
            <Reason tone="warning" role="alert">
              {problem.text(t)}
            </Reason>
          ) : null}
          <div>
            <Button onClick={onRetry} disabled={working || blockedId !== null} aria-busy={retrying ? "true" : undefined} aria-describedby={blockedId ?? undefined}>
              {retrying ? t("platformTelephony.retrying") : t("platformTelephony.retry")}
            </Button>
          </div>
        </>
      ) : null}
    </>
  );
}

type ReadyProps = { baseId: string; organizationId: string; view: TelephonyConnectionView; isTrial: boolean; blockedId: string | null };

/** Con la telefonia pronta: il numero mostrato, la registrazione e i numeri ammessi, e la prova della connessione. */
function ReadySections({ baseId, organizationId, view, isTrial, blockedId }: ReadyProps) {
  return (
    <>
      <CallerSection baseId={baseId} organizationId={organizationId} view={view} blockedId={blockedId} />
      <SettingsSection baseId={baseId} organizationId={organizationId} view={view} isTrial={isTrial} />
      <CheckSection baseId={baseId} organizationId={organizationId} view={view} />
    </>
  );
}

type SectionProps = { baseId: string; organizationId: string; view: TelephonyConnectionView };
type CallerProps = SectionProps & { blockedId: string | null };

/**
 * Il numero mostrato dell'azienda e la sua verifica presso il fornitore, con il codice da digitare sul telefono. Da R3 serve
 * soltanto a «Prova la connessione»: nelle chiamate il Lead vede il cellulare del parco di chi chiama (Impostazioni → Team).
 */
function CallerSection({ baseId, organizationId, view, blockedId }: CallerProps) {
  const t = useT();
  const verify = useVerifyCaller(organizationId);
  const [text, setText] = useState(view.defaultCallerNumber ?? "");
  // Il codice lo conosce soltanto questa pagina, dalla risposta alla richiesta: il server non lo conserva, e nemmeno la cache
  // delle scritture (si copia qui e si azzera subito, `ask`). `version` è quella della vista quando la risposta è arrivata.
  const [asked, setAsked] = useState<{ e164: string; verification: VerifyCallerResponse["verification"]; version: number } | null>(null);
  const [problem, setProblem] = useState<Problem | null>(null);
  const latestVersion = useRef(view.version);
  latestVersion.current = view.version;
  // Il codice serve a chi digita finché il fornitore aspetta: quando la vista nuova, riletta dopo la richiesta, non dice più
  // «chiesta» (confermata o fallita) lo si dimentica, e una richiesta di un collega che arrivasse dopo non lo fa riapparire.
  if (asked !== null && view.version > asked.version && !isVerifying(view.defaultCallerVerification)) setAsked(null);
  const fieldId = `${baseId}-caller`;
  const written = withoutSeparators(text.trim());
  // Lo stato è di un numero: se si scrive un altro numero non si dice niente di quello di prima.
  const state = written === view.defaultCallerNumber ? view.defaultCallerVerification : null;
  const instruction = asked !== null && asked.e164 === written && state === "requested" ? asked.verification : null;

  async function ask() {
    const number = e164Schema.safeParse(written);
    if (!number.success) {
      setProblem({ field: "caller", text: (tr) => tr("platformTelephony.callerInvalid") });
      document.getElementById(fieldId)?.focus();
      return;
    }
    setProblem(null);
    setText(number.data);
    try {
      const answer = await verify.mutateAsync({ e164: number.data });
      setAsked({ e164: number.data, verification: answer.verification, version: latestVersion.current });
      // Il codice resta soltanto qui: nella cache delle scritture non rimane (con `gcTime: 0` se ne va subito).
      verify.reset();
    } catch (error) {
      // Già verificato presso il fornitore (Task 20): parole del pannello, che non ha «Leggi i cellulari già verificati».
      const known = error instanceof ApiRequestError && error.code === "caller_number_already_verified";
      setProblem({ field: "request", text: (tr) => (known ? tr("platformTelephony.callerAlreadyVerified") : messageForError(tr, error)) });
    }
  }

  return (
    <section className="g-telephony__section" aria-labelledby={`${baseId}-caller-title`}>
      <h3 id={`${baseId}-caller-title`}>{t("platformTelephony.callerTitle")}</h3>
      <TextField
        id={fieldId}
        label={t("platformTelephony.callerNumber")}
        type="tel"
        value={text}
        autoComplete="off"
        spellCheck={false}
        hint={t("platformTelephony.callerHint")}
        error={problem?.field === "caller" ? problem.text(t) : undefined}
        onChange={(event) => {
          setText(event.target.value);
          setProblem(null);
        }}
      />
      <CallerVerification
        state={state}
        instruction={instruction}
        busy={verify.isPending}
        onVerify={() => void ask()}
        disabled={blockedId !== null}
        describedBy={blockedId ?? undefined}
        error={problem?.field === "request" ? problem.text(t) : null}
      />
    </section>
  );
}

type SettingsProps = SectionProps & { isTrial: boolean };

/**
 * Registrazione, avviso al Lead e numeri ammessi: si salvano insieme. La bozza ricorda la versione su cui è nata e il
 * salvataggio parte con quella (`settings-draft.ts`): se un collega ha salvato nel frattempo, il server risponde `409`, la
 * vista si rilegge, la bozza si rifonda sul nuovo tenendo ciò che era scritto e il pannello lo dice. Finché non c'è niente di
 * scritto la bozza segue la vista. La scadenza dell'audio non si sceglie qui: si rimanda quella della base.
 */
function SettingsSection({ baseId, organizationId, view, isTrial }: SettingsProps) {
  const t = useT();
  const save = useSaveTelephonySettings(organizationId);
  const [stored, setForm] = useState<SettingsForm>(() => startForm(view));
  // La vista letta può avere un'altra versione: la bozza la adotta, la segue o resta indietro (`reconcile`). Si decide nel
  // disegno stesso, così non c'è un istante con i valori vecchi.
  const form = reconcile(stored, view);
  if (form !== stored) setForm(form);
  const [problem, setProblem] = useState<Problem | null>(null);
  const [saved, setSaved] = useState(false);
  const noticeId = `${baseId}-notice`;
  const allowedId = `${baseId}-allowed`;
  const statusId = `${baseId}-save-status`;

  const { draft } = form;
  const dirty = isDirty(form);

  /** Cambia una voce della bozza: il «Salvato» di prima e i problemi non valgono più. */
  function edit(change: Partial<SettingsDraft>) {
    setForm((current) => ({ ...current, draft: { ...current.draft, ...change } }));
    setSaved(false);
    setProblem(null);
  }

  async function submit() {
    if (save.isPending) return;
    setSaved(false);
    const parsed = parseAllowedNumbers(draft.allowed);
    if (!parsed.ok) {
      setProblem(
        parsed.problem === "invalid"
          ? { field: "allowed", text: (tr) => tr("platformTelephony.allowedInvalid", { number: parsed.number }) }
          : { field: "allowed", text: (tr) => tr("platformTelephony.allowedTooMany", { max: MAX_ALLOWED_DESTINATIONS }) },
      );
      document.getElementById(allowedId)?.focus();
      return;
    }
    if (parsed.numbers.length === 0 && isTrial) {
      setProblem({ field: "allowed", text: (tr) => tr("platformTelephony.allowedMissing") });
      document.getElementById(allowedId)?.focus();
      return;
    }
    const body = telephonySettingsBodySchema.safeParse({
      recordingEnabled: draft.recording,
      recordingNotice: noticeOf(draft),
      // La scadenza dell'audio non si sceglie qui (questione 166): si rimanda quella della base.
      recordingRetentionDays: form.base.recordingRetentionDays,
      allowedDestinations: allowedOf(draft) ?? null,
      // La versione su cui è nata la bozza, non quella dell'ultima vista letta.
      expectedVersion: form.base.version,
    });
    if (!body.success) {
      const onNotice = body.error.issues.some((issue) => issue.path[0] === "recordingNotice");
      setProblem(
        onNotice
          ? { field: "notice", text: (tr) => tr("platformTelephony.noticeInvalid") }
          : { field: "form", text: (tr) => tr("errors.validation_failed") },
      );
      if (onNotice) document.getElementById(noticeId)?.focus();
      return;
    }
    setProblem(null);
    try {
      const { telephony } = await save.mutateAsync(body.data);
      // Il server ha ripulito il testo (spazi, a capo): la bozza torna a essere ciò che è salvato, con la sua versione.
      if (telephony) setForm(startForm(telephony));
      setSaved(true);
    } catch (error) {
      // Un collega ha salvato prima: la vista si rilegge da sé (`useTelephonyWrite`) e la bozza si rifonda sul nuovo.
      const conflict = error instanceof ApiRequestError && error.code === "version_conflict";
      if (conflict) setForm((current) => ({ ...current, conflicted: true }));
      setProblem({ field: "form", text: (tr) => (conflict ? tr("platformTelephony.conflict") : messageForError(tr, error)) });
    }
  }

  const status = save.isPending ? t("common.saving") : saved ? t("platformTelephony.saved") : !dirty ? t("platformTelephony.saveNothing") : null;
  return (
    <section className="g-telephony__section" aria-labelledby={`${baseId}-calls-title`}>
      <h3 id={`${baseId}-calls-title`}>{t("platformTelephony.callsTitle")}</h3>
      <form
        className="g-stack"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        {/* Durante il salvataggio i campi non cambiano: la risposta riporta la bozza a ciò che è salvato (rilievo di Codex sulla #73). */}
        <Checkbox
          id={`${baseId}-recording`}
          label={t("platformTelephony.recording")}
          checked={draft.recording}
          disabled={save.isPending}
          onChange={(checked) => edit({ recording: checked })}
        />
        <TextField
          id={noticeId}
          label={t("platformTelephony.recordingNotice")}
          value={draft.notice}
          maxLength={300}
          autoComplete="off"
          disabled={save.isPending}
          hint={t("platformTelephony.recordingNoticeHint")}
          error={problem?.field === "notice" ? problem.text(t) : undefined}
          onChange={(event) => edit({ notice: event.target.value })}
        />
        <TextArea
          id={allowedId}
          label={t("platformTelephony.allowed")}
          value={draft.allowed}
          rows={4}
          autoComplete="off"
          spellCheck={false}
          disabled={save.isPending}
          hint={`${t("platformTelephony.allowedHint")} ${t("platformTelephony.allowedFormat", { max: MAX_ALLOWED_DESTINATIONS })}`}
          error={problem?.field === "allowed" ? problem.text(t) : undefined}
          onChange={(event) => edit({ allowed: event.target.value })}
        />
        <LandlineWarnings text={draft.allowed} />
        {problem?.field === "form" ? (
          <Reason tone="warning" role="alert">
            {problem.text(t)}
          </Reason>
        ) : null}
        <div className="g-telephony__save">
          <Button
            variant="primary"
            type="submit"
            disabled={!dirty || save.isPending}
            aria-busy={save.isPending ? "true" : undefined}
            aria-describedby={statusId}
          >
            {t("platformTelephony.save")}
          </Button>
          {/* Sempre nella pagina e vuota finché non serve: uno screen reader annuncia l'esito del salvataggio. */}
          <p id={statusId} className="g-platform__wait" role="status">
            {status}
          </p>
        </div>
      </form>
    </section>
  );
}

/** «Prova la connessione»: soltanto lettura presso il fornitore; l'esito resta scritto con la data, anche a pannello riaperto. */
function CheckSection({ baseId, organizationId, view }: SectionProps) {
  const t = useT();
  const check = useCheckTelephony(organizationId);
  const [problem, setProblem] = useState<Problem | null>(null);

  async function run() {
    setProblem(null);
    try {
      await check.mutateAsync();
    } catch (error) {
      setProblem({ field: "check", text: (tr) => messageForError(tr, error) });
    }
  }

  const when = view.checkedAt === null ? null : t("platformTelephony.checkedAt", { date: formatNumericDateTime(deviceTimeZone(), view.checkedAt) });
  return (
    <section className="g-telephony__section" aria-labelledby={`${baseId}-check-title`}>
      <h3 id={`${baseId}-check-title`}>{t("platformTelephony.checkTitle")}</h3>
      <div>
        <Button onClick={() => void run()} disabled={check.isPending} aria-busy={check.isPending ? "true" : undefined}>
          {check.isPending ? t("platformTelephony.checking") : t("platformTelephony.check")}
        </Button>
      </div>
      {problem ? (
        <Reason tone="warning" role="alert">
          {problem.text(t)}
        </Reason>
      ) : null}
      {/* Sempre nella pagina e vuota finché non serve: uno screen reader annuncia l'esito della prova. */}
      <div role="status">
        {view.checkResult === "ok" ? (
          <StatusPill tone="success" icon="check">
            {t("platformTelephony.checkOk")}
          </StatusPill>
        ) : null}
        {view.checkResult !== null && view.checkResult !== "ok" ? <Reason tone="warning">{checkReason(t, view.checkResult)}</Reason> : null}
        {when ? <p className="g-platform__counts">{when}</p> : null}
      </div>
    </section>
  );
}
